// CallAnalyticsCharts.js
// Call analytics with volume trends, average duration, and peak hours

import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { 
  Phone, Clock, TrendingUp, TrendingDown, BarChart3, Calendar,
  PhoneIncoming, PhoneOutgoing, PhoneMissed, Activity, Users
} from 'lucide-react';
import { format, subDays, startOfDay, endOfDay, getHours, parseISO, isWithinInterval } from 'date-fns';

// Simple Bar Chart Component
const BarChart = ({ data, maxValue, color = 'bg-purple-500', height = 150 }) => {
  const max = maxValue || Math.max(...data.map(d => d.value), 1);
  
  return (
    <div className="flex items-end gap-1 h-full" style={{ height }}>
      {data.map((item, index) => (
        <div 
          key={index}
          className="flex-1 flex flex-col items-center gap-1"
        >
          <div className="relative w-full flex items-end justify-center" style={{ height: height - 24 }}>
            <div 
              className={`w-full max-w-8 rounded-t ${color} transition-all duration-300 hover:opacity-80`}
              style={{ 
                height: `${Math.max((item.value / max) * 100, item.value > 0 ? 5 : 0)}%`,
                minHeight: item.value > 0 ? '4px' : '0'
              }}
              title={`${item.label}: ${item.value}`}
            />
          </div>
          <span className="text-[10px] text-muted-foreground truncate w-full text-center">
            {item.label}
          </span>
        </div>
      ))}
    </div>
  );
};

// Line Chart Component (simple)
const LineChart = ({ data, color = '#7c3aed', height = 150 }) => {
  const max = Math.max(...data.map(d => d.value), 1);
  const points = data.map((d, i) => ({
    x: (i / (data.length - 1)) * 100,
    y: 100 - ((d.value / max) * 100)
  }));
  
  const pathD = points.length > 0 
    ? `M ${points[0].x},${points[0].y} ` + points.slice(1).map(p => `L ${p.x},${p.y}`).join(' ')
    : '';
  
  return (
    <div className="relative" style={{ height }}>
      <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="w-full h-full">
        {/* Grid lines */}
        <line x1="0" y1="25" x2="100" y2="25" stroke="#e5e7eb" strokeWidth="0.5" />
        <line x1="0" y1="50" x2="100" y2="50" stroke="#e5e7eb" strokeWidth="0.5" />
        <line x1="0" y1="75" x2="100" y2="75" stroke="#e5e7eb" strokeWidth="0.5" />
        
        {/* Area fill */}
        <path 
          d={`${pathD} L 100,100 L 0,100 Z`}
          fill={`${color}20`}
        />
        
        {/* Line */}
        <path 
          d={pathD}
          fill="none"
          stroke={color}
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        
        {/* Points */}
        {points.map((p, i) => (
          <circle 
            key={i}
            cx={p.x} 
            cy={p.y} 
            r="2" 
            fill={color}
          />
        ))}
      </svg>
      
      {/* X-axis labels */}
      <div className="flex justify-between mt-2 text-[10px] text-muted-foreground">
        {data.filter((_, i) => i % Math.ceil(data.length / 5) === 0 || i === data.length - 1).map((d, i) => (
          <span key={i}>{d.label}</span>
        ))}
      </div>
    </div>
  );
};

// Peak Hours Heatmap
const PeakHoursHeatmap = ({ hourlyData }) => {
  const maxCalls = Math.max(...hourlyData, 1);
  
  const getIntensity = (value) => {
    if (value === 0) return 'bg-slate-100';
    const ratio = value / maxCalls;
    if (ratio > 0.75) return 'bg-purple-600';
    if (ratio > 0.5) return 'bg-purple-400';
    if (ratio > 0.25) return 'bg-purple-200';
    return 'bg-purple-100';
  };
  
  return (
    <div className="space-y-2">
      <div className="grid grid-cols-12 gap-1">
        {hourlyData.slice(0, 12).map((value, hour) => (
          <div 
            key={hour}
            className={`h-8 rounded ${getIntensity(value)} flex items-center justify-center text-xs font-medium ${value > maxCalls * 0.5 ? 'text-white' : 'text-slate-600'}`}
            title={`${hour}:00 - ${value} calls`}
          >
            {value > 0 ? value : ''}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-12 gap-1">
        {hourlyData.slice(12, 24).map((value, hour) => (
          <div 
            key={hour + 12}
            className={`h-8 rounded ${getIntensity(value)} flex items-center justify-center text-xs font-medium ${value > maxCalls * 0.5 ? 'text-white' : 'text-slate-600'}`}
            title={`${hour + 12}:00 - ${value} calls`}
          >
            {value > 0 ? value : ''}
          </div>
        ))}
      </div>
      <div className="flex justify-between text-[10px] text-muted-foreground">
        <span>12 AM</span>
        <span>6 AM</span>
        <span>12 PM</span>
        <span>6 PM</span>
        <span>12 AM</span>
      </div>
    </div>
  );
};

// Main Analytics Component
export const CallAnalyticsCharts = ({ calls = [], dateRange = '7d' }) => {
  const [selectedRange, setSelectedRange] = useState(dateRange);
  
  // Calculate analytics based on date range
  const analytics = useMemo(() => {
    const now = new Date();
    const days = selectedRange === '7d' ? 7 : selectedRange === '30d' ? 30 : 90;
    const startDate = subDays(now, days);
    
    // Filter calls within range
    const filteredCalls = calls.filter(call => {
      if (!call.started_at) return false;
      const callDate = parseISO(call.started_at);
      return isWithinInterval(callDate, { start: startDate, end: now });
    });
    
    // Daily volume
    const dailyVolume = [];
    for (let i = days - 1; i >= 0; i--) {
      const date = subDays(now, i);
      const dayStart = startOfDay(date);
      const dayEnd = endOfDay(date);
      const count = filteredCalls.filter(c => {
        const callDate = parseISO(c.started_at);
        return isWithinInterval(callDate, { start: dayStart, end: dayEnd });
      }).length;
      dailyVolume.push({
        label: format(date, days <= 7 ? 'EEE' : 'M/d'),
        value: count,
        date
      });
    }
    
    // Hourly distribution (for peak hours)
    const hourlyDistribution = Array(24).fill(0);
    filteredCalls.forEach(call => {
      const hour = getHours(parseISO(call.started_at));
      hourlyDistribution[hour]++;
    });
    
    // Find peak hours
    const peakHour = hourlyDistribution.indexOf(Math.max(...hourlyDistribution));
    
    // Call types breakdown
    const inbound = filteredCalls.filter(c => c.direction === 'inbound').length;
    const outbound = filteredCalls.filter(c => c.direction === 'outbound').length;
    const missed = filteredCalls.filter(c => c.status === 'missed').length;
    const completed = filteredCalls.filter(c => c.status === 'completed').length;
    
    // Average duration (only completed calls)
    const completedCalls = filteredCalls.filter(c => c.duration_seconds > 0);
    const avgDuration = completedCalls.length > 0
      ? Math.round(completedCalls.reduce((sum, c) => sum + c.duration_seconds, 0) / completedCalls.length)
      : 0;
    
    // Total duration
    const totalDuration = filteredCalls.reduce((sum, c) => sum + (c.duration_seconds || 0), 0);
    
    // Trend comparison (compare to previous period)
    const previousStart = subDays(startDate, days);
    const previousCalls = calls.filter(call => {
      if (!call.started_at) return false;
      const callDate = parseISO(call.started_at);
      return isWithinInterval(callDate, { start: previousStart, end: startDate });
    });
    
    const volumeTrend = previousCalls.length > 0
      ? Math.round(((filteredCalls.length - previousCalls.length) / previousCalls.length) * 100)
      : 0;
    
    // Duration by day of week
    const dayOfWeekVolume = [
      { label: 'Sun', value: 0 },
      { label: 'Mon', value: 0 },
      { label: 'Tue', value: 0 },
      { label: 'Wed', value: 0 },
      { label: 'Thu', value: 0 },
      { label: 'Fri', value: 0 },
      { label: 'Sat', value: 0 }
    ];
    filteredCalls.forEach(call => {
      const dayIndex = parseISO(call.started_at).getDay();
      dayOfWeekVolume[dayIndex].value++;
    });
    
    return {
      totalCalls: filteredCalls.length,
      inbound,
      outbound,
      missed,
      completed,
      avgDuration,
      totalDuration,
      volumeTrend,
      peakHour,
      dailyVolume,
      hourlyDistribution,
      dayOfWeekVolume
    };
  }, [calls, selectedRange]);
  
  const formatDuration = (seconds) => {
    if (seconds < 60) return `${seconds}s`;
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}m ${secs}s`;
  };
  
  return (
    <div className="space-y-6">
      {/* Header with Date Range Selector */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <BarChart3 className="h-5 w-5 text-purple-600" />
            Call Analytics
          </h3>
          <p className="text-sm text-muted-foreground">
            Performance metrics and trends
          </p>
        </div>
        <Select value={selectedRange} onValueChange={setSelectedRange}>
          <SelectTrigger className="w-32">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="7d">Last 7 days</SelectItem>
            <SelectItem value="30d">Last 30 days</SelectItem>
            <SelectItem value="90d">Last 90 days</SelectItem>
          </SelectContent>
        </Select>
      </div>
      
      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Calls</p>
                <p className="text-2xl font-bold">{analytics.totalCalls}</p>
              </div>
              <div className={`flex items-center gap-1 text-sm ${analytics.volumeTrend >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                {analytics.volumeTrend >= 0 ? <TrendingUp className="h-4 w-4" /> : <TrendingDown className="h-4 w-4" />}
                {Math.abs(analytics.volumeTrend)}%
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Avg Duration</p>
                <p className="text-2xl font-bold">{formatDuration(analytics.avgDuration)}</p>
              </div>
              <Clock className="h-8 w-8 text-blue-200" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Answer Rate</p>
                <p className="text-2xl font-bold">
                  {analytics.totalCalls > 0 ? Math.round((analytics.completed / analytics.totalCalls) * 100) : 0}%
                </p>
              </div>
              <Activity className="h-8 w-8 text-emerald-200" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Peak Hour</p>
                <p className="text-2xl font-bold">
                  {analytics.peakHour !== -1 ? `${analytics.peakHour}:00` : 'N/A'}
                </p>
              </div>
              <Calendar className="h-8 w-8 text-purple-200" />
            </div>
          </CardContent>
        </Card>
      </div>
      
      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Call Volume Trend */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Call Volume Trend</CardTitle>
            <CardDescription>Daily call count over time</CardDescription>
          </CardHeader>
          <CardContent>
            {analytics.dailyVolume.length > 0 ? (
              <LineChart data={analytics.dailyVolume} color="#7c3aed" height={180} />
            ) : (
              <div className="h-[180px] flex items-center justify-center text-muted-foreground">
                No data available
              </div>
            )}
          </CardContent>
        </Card>
        
        {/* Call Types Breakdown */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Call Breakdown</CardTitle>
            <CardDescription>By direction and status</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-3">
                <div className="flex items-center justify-between p-2 bg-emerald-50 rounded">
                  <span className="flex items-center gap-2 text-sm">
                    <PhoneIncoming className="h-4 w-4 text-emerald-600" />
                    Inbound
                  </span>
                  <span className="font-semibold">{analytics.inbound}</span>
                </div>
                <div className="flex items-center justify-between p-2 bg-blue-50 rounded">
                  <span className="flex items-center gap-2 text-sm">
                    <PhoneOutgoing className="h-4 w-4 text-blue-600" />
                    Outbound
                  </span>
                  <span className="font-semibold">{analytics.outbound}</span>
                </div>
              </div>
              <div className="space-y-3">
                <div className="flex items-center justify-between p-2 bg-purple-50 rounded">
                  <span className="flex items-center gap-2 text-sm">
                    <Phone className="h-4 w-4 text-purple-600" />
                    Completed
                  </span>
                  <span className="font-semibold">{analytics.completed}</span>
                </div>
                <div className="flex items-center justify-between p-2 bg-red-50 rounded">
                  <span className="flex items-center gap-2 text-sm">
                    <PhoneMissed className="h-4 w-4 text-red-600" />
                    Missed
                  </span>
                  <span className="font-semibold">{analytics.missed}</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      
      {/* Peak Hours & Day of Week */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Peak Hours Heatmap */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Peak Hours</CardTitle>
            <CardDescription>Call distribution by hour of day</CardDescription>
          </CardHeader>
          <CardContent>
            <PeakHoursHeatmap hourlyData={analytics.hourlyDistribution} />
          </CardContent>
        </Card>
        
        {/* Day of Week */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Calls by Day of Week</CardTitle>
            <CardDescription>Which days are busiest</CardDescription>
          </CardHeader>
          <CardContent>
            <BarChart 
              data={analytics.dayOfWeekVolume} 
              color="bg-purple-500"
              height={150}
            />
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default CallAnalyticsCharts;
